/* eslint-disable no-undef -- TypeScript `type` / generics are misread by default ESLint parser */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Helmet } from "react-helmet";
import { Link, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { MessageCircle, Sparkles, TrendingUp } from "lucide-react";
import { APP_NAME } from "../../helpers/constants";
import { db } from "../../helpers/firebase";

const PAGE_BG = "#F8F9FA";

/** Demo page typography scale (body content). */
const DEMO_CARD_TITLE =
  "font-primary text-lg font-semibold tracking-tight text-white";
const DEMO_BODY =
  "max-w-prose text-pretty text-sm text-slate-600 leading-relaxed";
const DEMO_META =
  "text-[11px] font-bold uppercase tracking-wider text-slate-500";
/** Status chips: fixed height and padding across pipeline cards. */
const DEMO_STATUS_PILL =
  "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[11px] font-bold uppercase tracking-wider";

/** Pipeline preview cards: shared chrome (padding, border, shadow). */
const DEMO_PIPELINE_CARD =
  "overflow-hidden rounded-elegant border-2 border-[var(--color-border-light)] bg-[var(--color-bg-primary)] p-5 shadow-elegant sm:p-6";

const MOCK_NAME = "Bryan Edelman";
const MOCK_PROPERTY_ADDRESS = "123 Sandbox Lane";
const MOCK_UNIT_NAME = "Northside Duplex - Unit A";
const MOCK_ADDRESS_STREET = "123 Sandbox Lane";
const MOCK_BEDROOMS = 3;
const MOCK_BATHROOMS = 2;
const MOCK_MONTHLY_RENT = 4250;
const MOCK_CURRENT_VALUE = 485000;

/** Matches `listingModel` inquiry `creditScore` string values. */
const CREDIT_SCORE_LABEL: Record<string, string> = {
  excellent_720: "Excellent (720+)",
  good_650: "Good (650+)",
  fair_600: "Fair (600+)",
  unknown: "Not provided",
};

type DemoLead = {
  name: string | null;
  propertyAddress: string | null;
  unitName: string | null;
  addressStreet: string | null;
  monthlyRent: number | null;
  currentValue: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  isFallback: boolean;
};

function readFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function readAddressStreet(data: Record<string, unknown>): string | null {
  const addr = data.address;
  if (addr && typeof addr === "object" && !Array.isArray(addr)) {
    const street = (addr as Record<string, unknown>).street;
    if (typeof street === "string" && street.trim()) return street.trim();
  }
  if (typeof data.addressStreet === "string" && data.addressStreet.trim()) {
    return data.addressStreet.trim();
  }
  return null;
}

function readUnitName(data: Record<string, unknown>): string | null {
  if (typeof data.unitName === "string" && data.unitName.trim()) {
    return data.unitName.trim();
  }
  if (typeof data.propertyTitle === "string" && data.propertyTitle.trim()) {
    return data.propertyTitle.trim();
  }
  return null;
}

function leadFromFirestoreData(
  data: Record<string, unknown>,
  isFallback: boolean
): DemoLead {
  const name = typeof data.name === "string" ? data.name : null;
  const propertyAddress =
    typeof data.propertyAddress === "string"
      ? data.propertyAddress
      : null;
  const unitName = readUnitName(data);
  const addressStreet = readAddressStreet(data);
  const monthlyRent =
    readFiniteNumber(data.monthlyRent) ??
    readFiniteNumber(data.unitMonthlyRent);
  const currentValue =
    readFiniteNumber(data.currentValue) ??
    readFiniteNumber(data.estimatedValue);
  const bedrooms = readFiniteNumber(data.bedrooms);
  const bathrooms = readFiniteNumber(data.bathrooms);

  return {
    name,
    propertyAddress,
    unitName,
    addressStreet,
    monthlyRent,
    currentValue,
    bedrooms,
    bathrooms,
    isFallback,
  };
}

function ownerInitial(name: string | null): string {
  const t = name?.trim();
  if (!t) return "B";
  const ch = t.charAt(0);
  return ch.toUpperCase();
}

function formatUsd(amount: number | null): string {
  if (amount === null || !Number.isFinite(amount)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function OsSectionCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4 overflow-hidden rounded-elegant border-2 border-[var(--color-border-light)] bg-[var(--color-bg-primary)] shadow-elegant last:mb-0">
      <div className="flex items-center justify-center border-b-2 border-[var(--color-border-light)] bg-[#2C5F2D] px-5 py-4 sm:px-6 sm:py-5">
        <h3 className={`text-center ${DEMO_CARD_TITLE}`}>{title}</h3>
      </div>
      <div className="p-5 font-secondary sm:p-6">{children}</div>
    </div>
  );
}

function DemoLoading() {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-6 px-4 font-secondary antialiased"
      style={{ backgroundColor: PAGE_BG }}
    >
      <div className="relative h-14 w-14">
        <div className="absolute inset-0 rounded-full border-4 border-forest-200 border-t-[#2C5F2D] animate-spin" />
        <div className="absolute inset-2 rounded-full bg-forest-100" />
      </div>
      <p className="text-center text-base font-medium text-slate-600">
        Personalizing your experience…
      </p>
      <div className="mx-auto w-full max-w-md overflow-hidden rounded-elegant border-2 border-[var(--color-border-light)] bg-white p-6 shadow-soft-lg">
        <div className="h-4 w-1/3 animate-pulse rounded-full bg-slate-200" />
        <div className="mt-3 h-14 w-full animate-pulse rounded-soft bg-slate-100" />
        <div className="mt-4 flex gap-3">
          <div className="h-12 w-12 shrink-0 animate-pulse rounded-full bg-slate-200" />
          <div className="h-12 flex-1 animate-pulse rounded-soft bg-slate-100" />
        </div>
      </div>
    </div>
  );
}

const PERFORMANCE_BARS = [
  { label: "M", pct: 42 },
  { label: "T", pct: 58 },
  { label: "W", pct: 71 },
  { label: "T", pct: 55 },
  { label: "F", pct: 88 },
] as const;

function SamplePerformanceChart() {
  return (
    <div aria-labelledby="demo-performance-heading">
      <div className="mb-3 flex items-center gap-2">
        <TrendingUp
          className="h-5 w-5 shrink-0 text-[#2C5F2D]"
          aria-hidden
        />
        <h4
          id="demo-performance-heading"
          className={`font-primary ${DEMO_META} text-slate-600`}
        >
          Performance (sample)
        </h4>
      </div>
      <p className={`mb-4 max-w-prose ${DEMO_BODY}`}>
        Illustrative trend: connect your portfolio for live investor metrics.
      </p>
      <div
        className="flex h-40 items-end justify-between gap-2 border-b border-slate-200 px-1 pb-1"
        role="img"
        aria-label="Sample bar chart"
      >
        {PERFORMANCE_BARS.map((bar, index) => {
          const barHeightPx = Math.round((bar.pct / 100) * 120) + 28;
          return (
            <div
              key={`perf-${index}`}
              className="flex flex-1 flex-col items-center gap-2"
            >
              <div className="flex h-32 w-full items-end justify-center">
                <div
                  className="w-[72%] max-w-[3rem] rounded-t-soft bg-gradient-to-t from-[#2C5F2D] to-forest-400 shadow-sm"
                  style={{ height: `${barHeightPx}px` }}
                />
              </div>
              <span className={`${DEMO_META} text-slate-500`}>{bar.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CTA_SUBTEXT =
  "This is a static preview. Create a free account to use the live\u00A0console.";
const CTA_HEADLINE = "Get Your Live, Fully Data-Populated Dashboard";
const CTA_BUTTON = "Get Your Live Dashboard";

/** Single-line OpsChat + Leo label: same px, weight, and family (no uppercase mismatch). */
const DEMO_OPSCHAT_LEO_LABEL =
  "font-primary text-[11px] font-bold leading-tight tracking-wide";

/** OpsChat hero: pulsing live dot + status focused on tenant issue resolution. */
function LeoOpsChatHero() {
  return (
    <div className="relative overflow-hidden rounded-elegant border border-slate-200/90 bg-gradient-to-br from-slate-50/95 via-white to-forest-50/40 px-4 py-4 shadow-soft sm:px-5 sm:py-5">
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#CDAF63]/10 blur-2xl"
        aria-hidden
      />
      <div className="relative flex min-w-0 flex-col gap-2.5">
        <div className="flex items-center gap-3.5">
          <div
            className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center"
            aria-hidden
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2C5F2D] opacity-25" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-[#2C5F2D] shadow-sm ring-2 ring-white" />
          </div>
          <p className={`min-w-0 ${DEMO_OPSCHAT_LEO_LABEL}`}>
            <span className="text-slate-600">OpsChat</span>
            <span className="font-normal text-slate-400"> · </span>
            <span className="text-[#2C5F2D]">Leo AI</span>
          </p>
        </div>
        <p
          role="status"
          className="flex max-w-prose items-start gap-2 rounded-soft border border-slate-200/70 bg-slate-50/80 px-3 py-2.5 text-left text-pretty text-sm font-medium leading-relaxed text-slate-800 shadow-soft md:max-w-none"
        >
          <Sparkles
            className="mt-0.5 h-4 w-4 shrink-0 text-gold-500"
            aria-hidden
            strokeWidth={2}
          />
          <span className="min-w-0">
            {`Leo AI triages open tenant problems for this property so maintenance, access, and follow-ups stay in OpsChat instead of turning your phone into a\u00a0switchboard.`}
          </span>
        </p>
      </div>
    </div>
  );
}

function RentRollPreviewWidget({ lead }: { lead: DemoLead }) {
  const rent = lead.monthlyRent;
  const value = lead.currentValue;
  return (
    <div className="rounded-elegant border-2 border-[var(--color-border-light)] bg-[#F8F9FA] p-5 shadow-elegant sm:p-6">
      <p className={`font-primary ${DEMO_META} text-slate-600`}>
        Rent roll preview
      </p>
      <p className={`mt-2 ${DEMO_BODY}`}>
        Monthly rent vs. carrying value (investor-style snapshot).
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <p className={`${DEMO_META} text-slate-500`}>Monthly rent</p>
          <p className="mt-1 font-primary text-lg font-semibold text-[#2C5F2D]">
            {formatUsd(rent)}
          </p>
        </div>
        <div>
          <p className={`${DEMO_META} text-slate-500`}>Current value</p>
          <p className="mt-1 font-primary text-lg font-semibold text-slate-800">
            {formatUsd(value)}
          </p>
        </div>
      </div>
    </div>
  );
}

function OsAutomatedTag() {
  return (
    <p
      className={`mt-4 border-t border-[var(--color-border-light)] pt-3 text-left ${DEMO_META} text-slate-400`}
    >
      Automated by OS
    </p>
  );
}

function DashboardPreview({ lead }: { lead: DemoLead }) {
  const ownerLabel = lead.name?.trim() || "Account owner";
  const streetLine =
    lead.addressStreet?.trim() ||
    lead.propertyAddress?.trim() ||
    MOCK_ADDRESS_STREET;
  const unitTitle = lead.unitName?.trim() || null;
  const initial = ownerInitial(lead.name);
  const bed =
    lead.bedrooms != null ? lead.bedrooms : MOCK_BEDROOMS;
  const bath =
    lead.bathrooms != null ? lead.bathrooms : MOCK_BATHROOMS;

  const sampleMonthlyIncome = 6800;
  const sampleCreditKey = "good_650" as const;

  const ctaButtonClass =
    "inline-flex w-full items-center justify-center rounded-elegant px-5 py-3.5 text-center text-sm font-bold text-white shadow-soft-lg transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:min-w-[20rem] sm:w-auto";
  const ctaButtonStyle = {
    backgroundColor: "#2C5F2D",
    outlineColor: "#2C5F2D",
  } as const;

  return (
    <div className="mx-auto flex w-full max-w-full flex-col gap-6 sm:gap-8">
      {lead.isFallback && (
        <p className="mx-4 max-w-prose rounded-elegant border border-gold-200 bg-gold-50/90 px-4 py-3 text-center text-pretty text-sm font-medium leading-relaxed text-slate-800 shadow-soft sm:mx-0 sm:mx-auto">
          We couldn&apos;t load your personalized link. You&apos;re seeing a
          rich preview aligned to the {APP_NAME} OS.
        </p>
      )}

      <div className="flex flex-col items-stretch gap-5 px-4 text-center sm:px-0 sm:text-left lg:flex-row lg:items-center lg:justify-between lg:gap-8">
        <div className="min-w-0 flex-1">
          <p className={`mb-2 ${DEMO_META} text-slate-500`}>Active property</p>
          {unitTitle ? (
            <p className="font-primary text-xl font-bold leading-snug text-slate-900 sm:text-2xl">
              {unitTitle}
            </p>
          ) : null}
          <p className="font-primary text-3xl font-extrabold leading-[1.12] tracking-tight text-slate-900 md:text-5xl">
            {streetLine}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            <span className="font-secondary">{bed} bed</span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="font-secondary">{bath} bath</span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="font-secondary text-slate-500">
              From lead record when provided
            </span>
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-4 lg:w-auto lg:shrink-0 lg:flex-row-reverse lg:items-start lg:gap-5">
          <div
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-2xl font-bold text-white shadow-soft-lg ring-4 ring-forest-100"
            style={{ backgroundColor: "#2C5F2D" }}
            aria-hidden
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1 text-center sm:text-left lg:max-w-[15rem] lg:flex-none">
            <p className={`mb-2 ${DEMO_META} text-slate-500`}>Personal account</p>
            <p className="truncate font-primary text-lg font-semibold text-slate-900 sm:text-xl">
              {ownerLabel}
            </p>
            <p className={`mt-1 max-w-prose sm:max-w-none lg:max-w-none ${DEMO_BODY}`}>
              Prospect or owner name from the lead record when present.
            </p>
          </div>
        </div>
      </div>

      <div className="w-full overflow-hidden rounded-elegant border-2 border-[var(--color-border-light)] bg-white shadow-elegant sm:mx-0">
        <div className="p-5 sm:p-6">
          <OsSectionCard title="OpsChat · Live intelligence">
            <LeoOpsChatHero />
          </OsSectionCard>

          <OsSectionCard title="Today's pipeline">
            <ul className="flex flex-col gap-4">
              <li className={DEMO_PIPELINE_CARD}>
                <div className="flex flex-col gap-3 border-b border-[var(--color-border-light)] pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <MessageCircle
                      className="h-5 w-5 shrink-0 text-[#2C5F2D]"
                      aria-hidden
                    />
                    <span className="font-primary text-base font-semibold leading-tight text-slate-900 sm:text-lg">
                      Inquiry · Sarah Connor
                    </span>
                  </div>
                  <span
                    className={`${DEMO_STATUS_PILL} self-start border-[#CDAF63]/50 bg-gold-50 text-gold-900 sm:self-auto`}
                  >
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#CDAF63]"
                      aria-hidden
                    />
                    Scheduled
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3">
                    <div className="rounded-soft border border-[var(--color-border-light)] bg-white px-3 py-2.5 shadow-soft">
                      <p className={`${DEMO_META} text-slate-500`}>
                        Monthly income
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {formatUsd(sampleMonthlyIncome)}
                        <span className="text-xs font-normal text-slate-500">
                          {" "}
                          / mo
                        </span>
                      </p>
                    </div>
                    <div className="rounded-soft border border-[var(--color-border-light)] bg-white px-3 py-2.5 shadow-soft">
                      <p className={`${DEMO_META} text-slate-500`}>
                        Credit score
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {CREDIT_SCORE_LABEL[sampleCreditKey] ?? sampleCreditKey}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-500">
                    14 min ago · listing inquiry
                  </p>
                  <p
                    className={`${DEMO_BODY} font-semibold text-slate-800 md:max-w-none`}
                  >
                    Leo surfaced a high-intent inquiry: qualified tenant with
                    requests on file and a tour requested.
                  </p>
                </div>
              </li>

              <li className={DEMO_PIPELINE_CARD}>
                <div className="flex flex-col gap-3 border-b border-[var(--color-border-light)] pb-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 text-left">
                    <p className={`${DEMO_META} text-slate-500`}>
                      Open maintenance
                    </p>
                    <p className="mt-1 font-primary text-sm font-semibold text-slate-900">
                      MT-2841 · Plumbing · Unit 2B
                    </p>
                  </div>
                  <span
                    className={`${DEMO_STATUS_PILL} self-start border-blue-200 bg-blue-50 text-blue-900 sm:self-auto`}
                  >
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500"
                      aria-hidden
                    />
                    In bidding
                  </span>
                </div>
                <div className="mt-4 space-y-3 text-left">
                  <p className={`${DEMO_BODY} font-medium text-slate-700`}>
                    Priority High · SLA vendor under 4h
                  </p>
                  <p className={DEMO_BODY}>
                    Bath sink leak. Photos and shutoff note on file.
                  </p>
                  <div className="rounded-soft border border-[var(--color-border-light)] bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-700 shadow-soft">
                    <span className="font-semibold text-[#2C5F2D]">2 bids</span>
                    <span className="text-slate-400"> · </span>
                    Ace Plumbing $285 · Metro Rooter $310
                  </div>
                  <OsAutomatedTag />
                </div>
              </li>

              <li className={DEMO_PIPELINE_CARD}>
                <div className="flex flex-col gap-3 border-b border-[var(--color-border-light)] pb-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 text-left">
                    <p className={`${DEMO_META} text-slate-500`}>
                      Rent collected
                    </p>
                    <p className="mt-1 font-primary text-sm font-semibold text-slate-900">
                      April · ACH #LO-4892
                    </p>
                  </div>
                  <span
                    className={`${DEMO_STATUS_PILL} self-start border-emerald-200 bg-emerald-50 text-emerald-900 sm:self-auto`}
                  >
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
                      aria-hidden
                    />
                    On track
                  </span>
                </div>
                <div className="mt-4 space-y-3 text-left">
                  <p className={`${DEMO_BODY} font-medium text-slate-700`}>
                    <span className="font-semibold text-slate-800">
                      $4,250 cleared
                    </span>
                    <span className="font-normal text-slate-500"> · </span>
                    <span className="font-normal text-slate-600">
                      3 of 4 units
                    </span>
                  </p>
                  <p className={DEMO_BODY}>
                    <span className="font-semibold text-slate-800">Unit 1A</span>
                    <span className="font-normal">
                      : autopay nudge out, reminder opened at{" "}
                    </span>
                    <span className="font-normal">{`9:14\u00a0CT`}</span>
                  </p>
                  <p className={DEMO_BODY}>
                    <span className="font-semibold text-slate-800">
                      Next sweep
                    </span>
                    <span className="font-normal">
                      : May 1 · late-fee rule for 1A if balance remains
                    </span>
                  </p>
                  <OsAutomatedTag />
                </div>
              </li>
            </ul>
          </OsSectionCard>

          <OsSectionCard title="Rent roll preview">
            <RentRollPreviewWidget lead={lead} />
          </OsSectionCard>

          <OsSectionCard title="Performance">
            <SamplePerformanceChart />
          </OsSectionCard>
        </div>
      </div>

      <div className="hidden flex-col items-center gap-4 px-4 pb-2 text-center sm:flex sm:px-0">
        <p className={`mx-auto max-w-md text-center ${DEMO_BODY}`}>
          {CTA_SUBTEXT}
        </p>
        <Link
          to="/signup"
          className={ctaButtonClass}
          style={ctaButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-primary-dark)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#2C5F2D";
          }}
        >
          {CTA_HEADLINE}
        </Link>
      </div>

      {/* In-flow mobile footer (not fixed): avoids overlap with feedback widgets */}
      <footer className="mx-4 mt-2 flex flex-col gap-3 rounded-elegant border border-slate-200/90 bg-white/95 px-4 py-5 shadow-soft-lg backdrop-blur-md sm:hidden">
        <p className="text-center font-primary text-sm font-bold leading-snug text-slate-900">
          {CTA_HEADLINE}
        </p>
        <p className={`text-center ${DEMO_BODY}`}>{CTA_SUBTEXT}</p>
        <Link
          to="/signup"
          className={`${ctaButtonClass} py-4 text-base`}
          style={ctaButtonStyle}
        >
          {CTA_BUTTON}
        </Link>
        <div
          className="flex items-center justify-center gap-1.5 pt-1 text-gold-500"
          aria-hidden
        >
          <Sparkles className="h-4 w-4" strokeWidth={2} />
          <span className="font-primary text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
            Live OS preview
          </span>
        </div>
        <div
          className="h-[max(0.75rem,env(safe-area-inset-bottom))] shrink-0"
          aria-hidden
        />
      </footer>
    </div>
  );
}

function emptyLead(fallback: boolean): DemoLead {
  return {
    name: null,
    propertyAddress: null,
    unitName: null,
    addressStreet: null,
    monthlyRent: null,
    currentValue: null,
    bedrooms: null,
    bathrooms: null,
    isFallback: fallback,
  };
}

export default function DemoPage() {
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("leadId");
  const debug = useMemo(
    () => searchParams.get("debug")?.toLowerCase() === "true",
    [searchParams]
  );

  const [loading, setLoading] = useState(() => Boolean(leadId) && !debug);
  const [lead, setLead] = useState<DemoLead>(emptyLead(false));

  useEffect(() => {
    if (debug) {
      setLead({
        name: MOCK_NAME,
        propertyAddress: MOCK_PROPERTY_ADDRESS,
        unitName: MOCK_UNIT_NAME,
        addressStreet: MOCK_ADDRESS_STREET,
        monthlyRent: MOCK_MONTHLY_RENT,
        currentValue: MOCK_CURRENT_VALUE,
        bedrooms: MOCK_BEDROOMS,
        bathrooms: MOCK_BATHROOMS,
        isFallback: false,
      });
      setLoading(false);
      return;
    }

    if (!leadId) {
      setLead({
        ...emptyLead(false),
        propertyAddress: MOCK_PROPERTY_ADDRESS,
        unitName: MOCK_UNIT_NAME,
        addressStreet: MOCK_ADDRESS_STREET,
        monthlyRent: MOCK_MONTHLY_RENT,
        currentValue: MOCK_CURRENT_VALUE,
        bedrooms: MOCK_BEDROOMS,
        bathrooms: MOCK_BATHROOMS,
      });
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const snap = await getDoc(doc(db, "leads", leadId));
        if (cancelled) return;

        if (!snap.exists()) {
          setLead({
            name: null,
            propertyAddress: null,
            unitName: MOCK_UNIT_NAME,
            addressStreet: MOCK_ADDRESS_STREET,
            monthlyRent: MOCK_MONTHLY_RENT,
            currentValue: MOCK_CURRENT_VALUE,
            bedrooms: MOCK_BEDROOMS,
            bathrooms: MOCK_BATHROOMS,
            isFallback: true,
          });
          setLoading(false);
          return;
        }

        const data = (snap.data() ?? {}) as Record<string, unknown>;
        setLead(leadFromFirestoreData(data, false));
      } catch (e) {
        console.error("[DemoPage] Failed to fetch lead:", e);
        if (!cancelled) {
          setLead({
            name: null,
            propertyAddress: null,
            unitName: MOCK_UNIT_NAME,
            addressStreet: MOCK_ADDRESS_STREET,
            monthlyRent: MOCK_MONTHLY_RENT,
            currentValue: MOCK_CURRENT_VALUE,
            bedrooms: MOCK_BEDROOMS,
            bathrooms: MOCK_BATHROOMS,
            isFallback: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [leadId, debug]);

  const pageTitle = useMemo(() => {
    const addr =
      lead.addressStreet ||
      lead.propertyAddress ||
      (debug ? MOCK_ADDRESS_STREET : null);
    if (debug || addr) {
      return `Demo: ${addr ?? APP_NAME} | ${APP_NAME}`;
    }
    return `Interactive demo | ${APP_NAME}`;
  }, [debug, lead.addressStreet, lead.propertyAddress]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{`Loading demo | ${APP_NAME}`}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <DemoLoading />
      </>
    );
  }

  return (
    <div
      className="flex min-h-[100dvh] flex-col font-secondary text-slate-900 antialiased"
      style={{ backgroundColor: PAGE_BG }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-0 pb-24 pt-8 sm:px-6 sm:pb-10 sm:pt-14 md:max-w-4xl md:px-8 lg:max-w-5xl lg:px-10 xl:max-w-6xl xl:px-12 2xl:max-w-7xl">
        <p className="mb-6 px-4 text-center font-primary text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 sm:mb-8 sm:px-0">
          <span className="text-[#2C5F2D]">{APP_NAME}</span>
          <span className="text-slate-500"> · OS preview</span>
        </p>
        <div className="flex flex-1 flex-col">
          <DashboardPreview lead={lead} />
        </div>
      </main>
    </div>
  );
}
